// overlays.js
import { pointInPoly, overlayBounds, polyBounds, polyAreaAbs, convexHull, polyToPoints, pointsToPolyArray } from "./geometry.js";

function colorToHex(arr, fallbackHex, fallbackAlpha) {
  if (!Array.isArray(arr) || arr.length < 3) return { color: fallbackHex, alpha: fallbackAlpha };
  const r = Math.max(0, Math.min(255, arr[0] | 0));
  const g = Math.max(0, Math.min(255, arr[1] | 0));
  const b = Math.max(0, Math.min(255, arr[2] | 0));
  const a = (arr.length >= 4 && typeof arr[3] === "number") ? arr[3] : fallbackAlpha;
  return { color: (r<<16) | (g<<8) | b, alpha: a };
}

export class OverlayManager {
  constructor({ overlays, page, BASE_W, BASE_H, maskLayer, polyLayer }) {
    this.overlays = overlays;
    this.activeInfoLayerIndex = null; // null = show all
    this.page = page;
    this.BASE_W = BASE_W;
    this.BASE_H = BASE_H;

    // layers/graphics
    this.maskLayer = maskLayer;
    this.polyLayer = polyLayer;

    // TWO masks for crossfade
    this.maskA = new PIXI.Graphics(); this.maskA.alpha = 0;
    this.maskB = new PIXI.Graphics(); this.maskB.alpha = 0;
    this.maskLayer.addChild(this.maskA, this.maskB);
    this.currentMask = this.maskA;
    this.nextMask = this.maskB;

    // outlines
    this.baseOutline = new PIXI.Graphics();
    this.activeOutlineA = new PIXI.Graphics(); this.activeOutlineA.alpha = 0;
    this.activeOutlineB = new PIXI.Graphics(); this.activeOutlineB.alpha = 0;
    this.polyLayer.addChild(this.baseOutline, this.activeOutlineA, this.activeOutlineB);
    this.currentActiveOutline = this.activeOutlineA;
    this.nextActiveOutline = this.activeOutlineB;
  }

  setActiveInfoLayerIndex(idx) {
    this.activeInfoLayerIndex = (idx === null || idx === undefined) ? null : Number(idx);
    this.drawBase();
  }
  
  drawBase() {
    this.baseOutline.clear();
    for (const ov of this.overlays) {
      if (this.activeInfoLayerIndex !== null && ov.infoLayerIndex !== this.activeInfoLayerIndex) continue;
      const baseBorder = colorToHex(ov.border, 0xFFFFFF, 0.35);
      this.baseOutline.lineStyle(1, baseBorder.color, baseBorder.alpha);
      for (const poly of ov.polys) this.baseOutline.drawPolygon(poly);
    }
  }

  pickAtBasePoint(x, y) {
    let best = null;
    let bestArea = Infinity;

    for (let k = 0; k < this.overlays.length; k++) {
      const ov = this.overlays[k];
      if (this.activeInfoLayerIndex !== null && ov.infoLayerIndex !== this.activeInfoLayerIndex) continue;
      for (let pi = 0; pi < ov.polys.length; pi++) {
        const b = ov.bounds[pi];
        if (x < b.minx || x > b.maxx || y < b.miny || y > b.maxy) continue;
        const poly = ov.polys[pi];
        if (!pointInPoly(x, y, poly)) continue;

        const area = ov.areas[pi] ?? Infinity;
        const better = (area + 1e-9) < bestArea;
        const tie = Math.abs(area - bestArea) <= 1e-9;

        if (better || (tie && best && k >= (best._order ?? -1))) {
          best = { overlayId: ov.id, overlayName: ov.name, polyIndex: pi, masking: !!ov.masking, _order: k, _area: area };
          bestArea = area;
        } else if (tie && !best) {
          best = { overlayId: ov.id, overlayName: ov.name, polyIndex: pi, masking: !!ov.masking, _order: k, _area: area };
          bestArea = area;
        }
      }
    }
    return best;
  }

  buildInverseMaskInto(graphics, selection) {
    graphics.clear();
    if (!selection) return;

    const ov = this.overlays.find(o => o.id === selection.overlayId);
    if (!ov) return;
    if (!ov.masking && !this.page?.render?.masking) return;

    graphics.beginFill(0x000000, 0.55);
    graphics.drawRect(0, 0, this.BASE_W, this.BASE_H);
    graphics.beginHole();
    for (const poly of ov.polys) graphics.drawPolygon(poly);
    graphics.endHole();
    graphics.endFill();
  }

  buildActiveOutlineInto(graphics, selection) {
    graphics.clear();
    if (!selection) return;

    const ov = this.overlays.find(o => o.id === selection.overlayId);
    if (!ov) return;

    const hiBorder = colorToHex(ov.highlightBorder, 0xFFFFFF, 0.95);
    graphics.lineStyle(1, hiBorder.color, hiBorder.alpha);
    for (const poly of ov.polys) graphics.drawPolygon(poly);
  }

  swapAfterFade() {
    // swap masks
    this.currentMask.alpha = 0;
    this.currentMask.clear();
    const tmpM = this.currentMask;
    this.currentMask = this.nextMask;
    this.nextMask = tmpM;

    // swap outlines
    this.currentActiveOutline.alpha = 0;
    this.currentActiveOutline.clear();
    const tmpO = this.currentActiveOutline;
    this.currentActiveOutline = this.nextActiveOutline;
    this.nextActiveOutline = tmpO;
  }

  getOverlayById(id) {
    return this.overlays.find(o => o.id === id) || null;
  }

  computeViewToFitBounds(app, view, bounds, paddingPx, MIN_SCALE, MAX_SCALE) {
    const vw = app.renderer.width;
    const vh = app.renderer.height;
    const pad = Math.max(0, paddingPx || 0);
    const availW = Math.max(10, vw - 2*pad);
    const availH = Math.max(10, vh - 2*pad);

    const bw = Math.max(1e-6, (bounds.maxx - bounds.minx));
    const bh = Math.max(1e-6, (bounds.maxy - bounds.miny));

    let scale = Math.min(availW / bw, availH / bh);
    scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));

    const cx = (bounds.minx + bounds.maxx) / 2;
    const cy = (bounds.miny + bounds.maxy) / 2;

    return { scale, tx: vw/2 - cx * scale, ty: vh/2 - cy * scale };
  }

  overlayBounds(ov) {
    return overlayBounds(ov);
  }
    // --- metrics helpers (needed after editing) ---
    _recomputeOverlayMetrics(ov) {
      ov.bounds = ov.polys.map(polyBounds);
      ov.areas  = ov.polys.map(polyAreaAbs);
  
      const allPts = [];
      for (const poly of ov.polys) allPts.push(...polyToPoints(poly));
      const hullPts = convexHull(allPts);
      ov.hull = pointsToPolyArray(hullPts);
    }
  
    recomputeAll() {
      for (const ov of this.overlays) this._recomputeOverlayMetrics(ov);
    }
  
    // --- CRUD ---
    addPoly(overlayId, poly) {
      const ov = this.getOverlayById(overlayId);
      if (!ov) return false;
      ov.polys.push(poly);
      this._recomputeOverlayMetrics(ov);
      this.drawBase();
      return true;
    }
  
    updatePoly(overlayId, polyIndex, poly) {
      const ov = this.getOverlayById(overlayId);
      if (!ov || polyIndex < 0 || polyIndex >= ov.polys.length) return false;
      ov.polys[polyIndex] = poly;
      this._recomputeOverlayMetrics(ov);
      this.drawBase();
      return true;
    }
  
    deletePoly(overlayId, polyIndex) {
      const ov = this.getOverlayById(overlayId);
      if (!ov || polyIndex < 0 || polyIndex >= ov.polys.length) return false;
      ov.polys.splice(polyIndex, 1);
      if (!ov.polys.length) {
        // keep overlay object, but it has no geometries now
        ov.bounds = [];
        ov.areas = [];
        ov.hull = [];
      } else {
        this._recomputeOverlayMetrics(ov);
      }
      this.drawBase();
      return true;
    }
  
}
